// Offline mode: the one connection to the host's offline engine, shared by the banner, the Offline mode page, the staff lock and the
// capability gates. Importing this file does nothing; startOfflineMonitor() (called once from index.js) starts it, and only when the
// API base is a loopback address. With no engine every hook below answers "online, everything allowed".
import { useSyncExternalStore } from 'react';
import axios from 'axios';
import { defaultConfig } from '../config';
import { createConnection } from './engine';
import { canDo, isOfflineState } from './state';

export const connection = createConnection({ baseUrl: defaultConfig.baseAPIUrl });

export const getConnection = () => connection.getSnapshot();

let started = false;

/** Starts polling the engine (idempotent). Also reads the X-OA-Mode header off the API responses the page already gets. */
export function startOfflineMonitor() {
  if (started) return;
  started = true;
  if (!connection.start()) return; // not a loopback API base: no engine can exist, nothing is installed
  const note = (headers) => connection.noteMode(headers?.get?.('x-oa-mode') ?? headers?.['x-oa-mode']);
  axios.interceptors.response.use(
    (res) => {
      note(res?.headers);
      return res;
    },
    (err) => {
      note(err?.response?.headers);
      return Promise.reject(err);
    },
  );
  const onFocus = () => connection.refresh();
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) onFocus();
  });
}

/** { state, offline, engine, canForce, device, staff, headerOnly }: re-renders when the engine's answer changes. */
export function useConnection() {
  const snap = useSyncExternalStore(connection.subscribe, connection.getSnapshot);
  return { ...snap, offline: isOfflineState(snap.state) };
}

/** Online: always true. Offline: whether the engine lists this capability as working (cardPayments, payLink, vouchers, refunds, ...). */
export function useCapability(name) {
  return canDo(useSyncExternalStore(connection.subscribe, connection.getSnapshot).state, name);
}

export { canDo };
